/*
 * Copyright (c) 2018 - 2020 CalAmp Corp.  All Rights Reserved
 */
/* eslint-disable no-console */
import isArray from 'lodash.isarray';
import isObject from 'lodash.isobject';
import {contains} from '../utils/StringUtil';

/**
 * Defines the application prefix used in all log messages. This helps aid in
 * filtering logs by term e.g. '[CalAmp iOn]'.
 *
 * The value is statically defined (when using nextjs) by either NEXT_PUBLIC_APP_VERSION
 * in next.config, or defined via process.env.APP_NAME, and falling back to 'CalAmp iOn'
 * if neither are provided.
 */
export const APPLICATION = process.env.NEXT_PUBLIC_APP_NAME || process.env.APP_NAME || 'CalAmp iOn';

/**
 * Determines if the logger is enabled or not. This value must only be
 * set via the enable function.
 */
let _enabled = typeof console !== 'undefined' && process.env.NODE_ENV !== 'test';

/**
 * @function enable
 *
 * Enables / Disables the logger. Note, this value is to be set via a property
 * from the environments.json configuration, or another external means (such as
 * a query string parameter, an environment variable, etc.).
 *
 * Provides the single entry point for logging errors in iOn Suite.
 *
 * ```typescript
 *
 *   import {enable} from '@ion/core/src/logger/logger';
 *   import {get} from '@ion/core/src/environment/environment';
 *
 *   enable(get('enableLogging'));
 *
 * ```
 *
 */
export const enable = (val: boolean, force = false) => {
  _enabled = (val && _enabled) || force;
  if (_enabled) {
    const header = 'CalAmp';
    const styles = 'font-weight: bold; font-size: 22px; color: #02ABCA;';
    console.log('%c---------------------------------', styles);
    console.log(`%c${!contains(APPLICATION, header) ? header : ''} ${APPLICATION}`, styles);
    console.log('%c---------------------------------', styles);
  }
};

/**
 * Provides a convenience for determining if application logging is currently
 * enabled. This utility is primarily intended for API specific loggers to
 * allow for omitting logging functionality if logging is disabled globally.
 *
 * @returns `true` if application monitoring is globally enabled, otherwise,
 *          `false`.
 */
export const isEnabled = (): boolean => _enabled;

/**
 * Defines the default logger, all public logging functions are to delegate
 * to this function.
 */
const logger = (msg: string, type = 'log') => {
  console?.[type](`%c[${APPLICATION}]:%c ${msg}`, 'font-weight: bold', 'font-weight: normal');
};

/**
 * @function info
 *
 * Provides the single entry point for logging info in iOn Suite.
 *
 * ```typescript
 *
 *   import {info} from '@ion/core/src/logger/logger';
 *
 *   info('This is an Info Log ...', 'abc', 123);
 *   // [CalAmp iOn Suite] This is an Info Log ..., ABC, 123
 *
 * ```
 *
 */
export const info = (...args) => {
  _enabled && logger(formatArgs(...args), 'info');
};

/**
 * @function warn
 *
 * Provides the single entry point for logging warnings in iOn Suite.
 *
 * ```typescript
 *
 *   import {warn} from '@ion/core/src/logger/logger';
 *
 *   warn('This is a Warning Log ...', 'abc', 123);
 *   // [CalAmp iOn Suite] This is a Warning Log ..., ABC, 123
 *
 * ```
 *
 */
export const warn = (...args) => {
  _enabled && logger(formatArgs(...args), 'warn');
};

/**
 * @function error
 *
 * Provides the single entry point for logging errors in iOn Suite.
 *
 * ```typescript
 *
 *   import {error} from '@ion/core/src/logger/logger';
 *
 *   error('This is an Error Log ...', 'abc', 123);
 *   // [CalAmp iOn Suite] This is an Error Log ..., ABC, 123
 *
 *   error(new Error('This is an Error Log ...'), 'abc', 123);
 *   // [CalAmp iOn Suite] Error: This is an Error Log ...
 *   //   at (stack trace) ...
 *   // ABC, 123
 *
 * ```
 *
 */
export const error = (...args) => {
  if (_enabled) {
    if (args[0] instanceof Error) {
      args = [args[0].stack, ...args.slice(1, args.length)];
    }
    logger(formatArgs(...args), 'error');
  }
};

/**
 * Provides a convenience logger which allows for logging a value directly to the
 * console. Generally, this logger should be used for logging raw values directly
 * rather than having the values pre-formatted via the logger. This function is
 * essentially a wrapper around `console.log` with the exception that the call is
 * bypassed when logging is disable.
 *
 * ```typescript
 *
 *   import {info, log} from '@ion/core/src/logger/logger';
 *
 *   info('This is a Formatted Message');
 *   // [CalAmp iOn] This is a formatted message
 *
 *   log('This is an Unformatted Message');
 *   // This is an Unformatted Message
 *
 * ```
 */
export const log = (item) => {
  if (_enabled) {
    console.log(item);
  }
};

/**
 * Provides a convenience logger which allows for logging an arbitrary list
 * of values without pre-pending the log with the logger header. This utility
 * is generally provided for logging raw values.
 */
export const values = (...args) => {
  console.info(formatArgs(...args));
};

/**
 * Provides a simple utility for formatting logger arguments ...
 */
const formatArgs = (...args) => {
  let message = '';
  args.forEach(arg => {
    if (isObject(arg)) {
      Object.keys(arg).forEach((prop, i) => {
        if (i === 0) {
          message += '\n';
        }
        message += `\t${prop}: ${arg[prop]}`;
      });
    } else if (isArray(arg)) {
      message += JSON.stringify(arg);
    } else {
      message += arg;
    }
    message += ' ';
  });
  return message.trim();
};
